
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import DateTimePicker from '@/components/shared/DateTimePicker';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import { DialogClose } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, CalendarIcon, Video, ExternalLink } from 'lucide-react';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';


// Create schema factory with past date validation for both creating and editing
const createAppointmentSchema = (isEditing: boolean = false) => {
  const recurrenceSchema = z.object({
    frequency: z.enum(['none', 'daily', 'weekly', 'monthly']).default('none'),
    weeklyDays: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).optional(),
    monthlyDates: z.array(z.number().min(1).max(31)).optional(),
    endDate: z.date().optional(),
  }).superRefine((data, ctx) => {
    if (data.frequency === 'weekly' && (!data.weeklyDays || data.weeklyDays.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one day of the week',
        path: ['weeklyDays'],
      });
    }
    if (data.frequency === 'monthly' && (!data.monthlyDates || data.monthlyDates.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one date of the month',
        path: ['monthlyDates'],
      });
    }
  });

  const baseSchema = z.object({
    clientId: z.string().min(1, { message: 'Client selection is required' }),
    title: z.string().min(1, { message: 'Title is required' }),
    date: z.date({ required_error: "Date is required" }),
    time: z.string().min(1, { message: 'Start time is required' }),
    endTime: z.string().min(1, { message: 'End time is required' }),
    timezone: z.string().min(1, { message: 'Timezone is required' }),
    description: z.string().optional(),
    createMeetLink: z.boolean().default(false),
    additionalAttendees: z.string().optional(),
    recurrence: recurrenceSchema,
    updateScope: z.enum(['this_only', 'this_and_following', 'all']).optional(),
    deleteScope: z.enum(['this_only', 'this_and_following', 'all']).optional(),
  })
    .refine((data) => {
      // Validate that end time is after start time
      const [startHours, startMinutes] = data.time.split(':').map(Number);
      const [endHours, endMinutes] = data.endTime.split(':').map(Number);

      const startTimeMinutes = startHours * 60 + startMinutes;
      const endTimeMinutes = endHours * 60 + endMinutes;

      return endTimeMinutes > startTimeMinutes;
    }, {
      message: "End time must be after start time",
      path: ["endTime"],
    });

  // Validate past dates/times for both creating and editing appointments
  return baseSchema
    .refine((data) => {
      if (data.recurrence.endDate) {
        const startDay = new Date(data.date);
        startDay.setHours(0, 0, 0, 0);
        const end = new Date(data.recurrence.endDate);
        end.setHours(0, 0, 0, 0);
        return end >= startDay;
      }
      return true;
    }, {
      message: "Recurrence end date cannot be before start date",
      path: ["recurrence", "endDate"],
    })
    .refine((data) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(data.date);
      selectedDate.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, {
      message: "Cannot create or update appointments for past dates",
      path: ["date"]
    })
    .refine((data) => {
      // Check if date and time combination is in the past
      const now = new Date();
      const [hours, minutes] = data.time.split(':').map(Number);
      const appointmentDateTime = new Date(data.date);
      appointmentDateTime.setHours(hours, minutes, 0, 0);

      return appointmentDateTime >= now;
    }, {
      message: "Cannot create or update appointments in the past",
      path: ["time"], // Attach error to time field
    });
};

export type AppointmentFormValues = z.infer<ReturnType<typeof createAppointmentSchema>>;

interface AppointmentFormProps {
  onSubmit: (data: AppointmentFormValues) => Promise<void>;
  defaultValues?: Partial<AppointmentFormValues>;
  disabledDates?: Date[];
  isEditing?: boolean;
  isRecurring?: boolean;
  isModified?: boolean;
  isParent?: boolean;
  onDelete?: (deleteScope: 'this_only' | 'this_and_following' | 'all') => void;
  appointmentId?: string | number;
  appointment?: any; // Full appointment object to access meetingLink
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  onSubmit,
  defaultValues,
  disabledDates = [],
  isEditing = false,
  isRecurring = false,
  isModified = false,
  isParent = false,
  onDelete,
  appointmentId,
  appointment,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Get today's date for disabling past dates (for both creating and editing)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Function to check if a date is in the past
  const isPastDate = (date: Date) => {
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck < today;
  };

  // Combine disabled dates with past dates - Calendar accepts function or Date[]
  // Always disable past dates for both creating and editing
  const allDisabledDates = disabledDates.length > 0
    ? (date: Date) => {
      // Check if date is in the disabledDates array
      const isDisabled = disabledDates.some(disabledDate => {
        const d1 = new Date(disabledDate);
        const d2 = new Date(date);
        d1.setHours(0, 0, 0, 0);
        d2.setHours(0, 0, 0, 0);
        return d1.getTime() === d2.getTime();
      });
      return isDisabled || isPastDate(date);
    }
    : isPastDate;

  // Get default time (next hour, rounded)
  const getDefaultTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0); // Next hour, round minutes to 0
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  // Get default end time (1 hour after start time)
  const getDefaultEndTime = (startTime?: string) => {
    if (startTime) {
      const [hours, minutes] = startTime.split(':').map(Number);
      const endTime = new Date();
      endTime.setHours(hours + 1, minutes, 0, 0); // 1 hour after start
      return `${String(endTime.getHours()).padStart(2, '0')}:${String(endTime.getMinutes()).padStart(2, '0')}`;
    }
    const now = new Date();
    now.setHours(now.getHours() + 2, 0, 0, 0); // 2 hours from now
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const appointmentSchema = createAppointmentSchema(isEditing);

  const defaultStartTime = getDefaultTime();
  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      clientId: '',
      title: '',
      date: new Date(), // Initialize with current date to prevent Invalid Date errors
      time: defaultStartTime,
      endTime: getDefaultEndTime(defaultStartTime),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      description: '',
      createMeetLink: false,
      additionalAttendees: '',
      recurrence: {
        frequency: 'none',
        weeklyDays: [],
        monthlyDates: [],
        endDate: undefined,
      },
      updateScope: 'this_only',
      deleteScope: 'this_only',
      ...defaultValues,
    }
  });

  const startDate = form.watch('date');
  const recurrenceFrequency = form.watch('recurrence.frequency');
  const recurrenceEndDate = form.watch('recurrence.endDate');

  const recurrenceSummary = useMemo(() => {
    let baseText = '';
    if (recurrenceFrequency === 'daily') {
      baseText = 'Repeats every day';
    } else if (recurrenceFrequency === 'weekly') {
      baseText = 'Repeats on selected weekdays';
    } else if (recurrenceFrequency === 'monthly') {
      baseText = 'Repeats monthly on chosen dates';
    } else {
      return 'One-time appointment';
    }

    if (recurrenceEndDate) {
      return `${baseText} until ${format(recurrenceEndDate, 'MMM d, yyyy')}`;
    }
    return `${baseText} (forever)`;
  }, [recurrenceFrequency, recurrenceEndDate]);

  const handleFormSubmit = async (data: AppointmentFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Meeting Title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Client</FormLabel>
                <FormControl>
                  <SearchableClientDropdown
                    value={field.value}
                    onChange={(clientId, client) => {
                      field.onChange(clientId?.toString());
                    }}
                    placeholder="Search and select client..."
                    error={form.formState.errors.clientId?.message}
                    appointmentId={appointmentId}
                    displayVariant="questionnaire"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date and Start Time</FormLabel>
                <FormControl>
                  <DateTimePicker
                    date={field.value}
                    onDateChange={(newDate) => {
                      // Only allow date change if not editing a recurring appointment
                      if (!(isEditing && isRecurring)) {
                        field.onChange(newDate);
                        // Re-validate time when date changes
                        form.trigger('time');
                        form.trigger('endTime');
                      }
                    }}
                    time={form.watch('time')}
                    onTimeChange={(time) => {
                      form.setValue('time', time, { shouldValidate: true });
                      // Auto-update end time if it's before or equal to start time
                      const currentEndTime = form.watch('endTime');
                      if (currentEndTime) {
                        const [startHours, startMinutes] = time.split(':').map(Number);
                        const [endHours, endMinutes] = currentEndTime.split(':').map(Number);
                        const startTimeMinutes = startHours * 60 + startMinutes;
                        const endTimeMinutes = endHours * 60 + endMinutes;
                        if (endTimeMinutes <= startTimeMinutes) {
                          // Set end time to 1 hour after start time
                          const newEndTime = new Date();
                          newEndTime.setHours(startHours + 1, startMinutes, 0, 0);
                          form.setValue('endTime', `${String(newEndTime.getHours()).padStart(2, '0')}:${String(newEndTime.getMinutes()).padStart(2, '0')}`, { shouldValidate: true });
                        }
                      }
                    }}
                    disabled={allDisabledDates}
                    minDate={today}
                    allowPastDates={false}
                    disableDatePicker={isEditing && isRecurring}
                  />
                </FormControl>
                <FormMessage />
                {form.formState.errors.time && (
                  <p className="text-sm font-medium text-destructive">{form.formState.errors.time.message}</p>
                )}
                {isEditing && isRecurring && (
                  <p className="text-sm text-muted-foreground">Date cannot be changed for recurring appointments. Only time can be modified.</p>
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End Time</FormLabel>
                <FormControl>
                  <Input
                    type="time"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form.trigger('endTime');
                    }}
                    className="h-10"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />


          <FormField
            control={form.control}
            name="additionalAttendees"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Additional Attendees (Emails)</FormLabel>
                <FormControl>
                  <Input placeholder="email1@example.com, email2@example.com" {...field} />
                </FormControl>
                <FormDescription>Comma separated list of emails</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Meeting description"
                    className="min-h-[80px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {!isEditing && (
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Recurrence</p>
                <p className="text-xs text-muted-foreground">{recurrenceSummary}</p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="recurrence.frequency"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="grid grid-cols-1 md:grid-cols-2 gap-2"
                  >
                    {[
                      { value: 'none', label: 'Do Not Repeat', description: 'One-time appointment' },
                      { value: 'daily', label: 'Daily', description: 'Repeats every day' },
                      { value: 'weekly', label: 'Weekly', description: 'Select days of week' },
                      { value: 'monthly', label: 'Monthly', description: 'Repeats on a date' },
                    ].map(option => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3 rounded-md border px-3 py-2"
                      >
                        <RadioGroupItem value={option.value} id={`recurrence-${option.value}`} />
                        <div className="space-y-1">
                          <Label htmlFor={`recurrence-${option.value}`} className="text-sm font-medium">
                            {option.label}
                          </Label>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </FormItem>
              )}
            />

            {recurrenceFrequency === 'weekly' && (
              <div className="space-y-3">
                <FormLabel>Select days of the week</FormLabel>
                <FormField
                  control={form.control}
                  name="recurrence.weeklyDays"
                  render={({ field }) => {
                    type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
                    const value = (field.value || []) as DayKey[];
                    const toggleDay = (day: DayKey) => {
                      const updated = value.includes(day)
                        ? value.filter((d) => d !== day)
                        : [...value, day];
                      field.onChange(updated);
                    };
                    const days: { key: DayKey; label: string }[] = [
                      { key: 'mon', label: 'Mon' },
                      { key: 'tue', label: 'Tue' },
                      { key: 'wed', label: 'Wed' },
                      { key: 'thu', label: 'Thu' },
                      { key: 'fri', label: 'Fri' },
                      { key: 'sat', label: 'Sat' },
                      { key: 'sun', label: 'Sun' },
                    ];
                    return (
                      <FormItem className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                          {days.map((day) => (
                            <label
                              key={day.key}
                              className={cn(
                                "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
                                value.includes(day.key) ? "border-primary bg-primary/5" : "border-muted"
                              )}
                            >
                              <Checkbox
                                checked={value.includes(day.key)}
                                onCheckedChange={() => toggleDay(day.key)}
                                id={`recurrence-day-${day.key}`}
                              />
                              <span>{day.label}</span>
                            </label>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            )}

            {recurrenceFrequency === 'monthly' && (
              <div className="space-y-2">
                <FormLabel>Select dates of the month</FormLabel>
                <FormField
                  control={form.control}
                  name="recurrence.monthlyDates"
                  render={({ field }) => {
                    const selected = (field.value || []) as number[];
                    const toggleDate = (day: number) => {
                      const updated = selected.includes(day)
                        ? selected.filter((d) => d !== day)
                        : [...selected, day].sort((a, b) => a - b);
                      field.onChange(updated);
                    };
                    const days = Array.from({ length: 31 }, (_, idx) => idx + 1);
                    return (
                      <FormItem className="space-y-2">
                        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
                          {days.map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleDate(day)}
                              className={cn(
                                "flex h-10 w-10 items-center justify-center rounded-md border text-sm transition",
                                selected.includes(day)
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-muted bg-background hover:border-primary/60"
                              )}
                              aria-pressed={selected.includes(day)}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                        <FormDescription>Pick one or more dates</FormDescription>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            )}

            {['weekly', 'monthly', 'daily'].includes(recurrenceFrequency) && (
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="recurrence.endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-2">
                      <FormLabel>End date (optional)</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full sm:w-[240px] max-w-full justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : "Pick an end date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              if (!date) return;
                              field.onChange(date);
                            }}
                            initialFocus
                            disabled={(date) => {
                              if (!startDate) return false;
                              const start = new Date(startDate);
                              start.setHours(0, 0, 0, 0);
                              const check = new Date(date);
                              check.setHours(0, 0, 0, 0);
                              return check < start;
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormDescription>Leave empty to repeat forever</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        <FormField
          control={form.control}
          name="createMeetLink"
          render={({ field }) => {
            // Get meeting link from appointment data
            const appointmentData = appointment as any;
            const existingMeetingLink = appointmentData?.meetingLink || appointmentData?.meeting_link || null;
            
            return (
              <FormItem className="space-y-3">
                <div className="flex flex-row items-center justify-between gap-3 rounded-lg border p-3 sm:p-4">
                  <div className="space-y-0.5 min-w-0">
                    <FormLabel className="text-base">
                      Create Google Meet Link
                    </FormLabel>
                    <FormDescription>
                      Turn on for online appointments
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </div>
                
                {/* Display existing meeting link when editing */}
                {isEditing && existingMeetingLink && (
                  <div className="rounded-lg border p-4 bg-muted/30">
                    <div className="flex items-start gap-3">
                      <Video className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0 space-y-1">
                        <FormLabel className="text-sm font-medium">Meeting Link</FormLabel>
                        <a 
                          href={existingMeetingLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium inline-flex items-center gap-1 break-all"
                        >
                          {existingMeetingLink}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                        <FormDescription className="text-xs mt-1">
                          This is the existing meeting link for this appointment
                        </FormDescription>
                      </div>
                    </div>
                  </div>
                )}
              </FormItem>
            );
          }}
        />

        {isEditing && isRecurring && (
          <FormField
            control={form.control}
            name="updateScope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Update scope</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="this_only">This event only</option>
                    {!isModified && <option value="this_and_following">This and following events</option>}
                    {!isModified && <option value="all">All events</option>}
                  </select>
                </FormControl>
                <FormDescription>
                  Choose which appointments in this series you want to update
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}


        <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pt-2">
          {isEditing && onDelete && (
            <Button 
              type="button" 
              variant="destructive" 
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Delete
            </Button>
          )}
          {!isEditing && <div className="hidden sm:block" />}
          <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
            <DialogClose asChild>
              <Button variant="outline" type="button" disabled={isSubmitting} className="w-full sm:w-auto">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update' : 'Create'
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {isRecurring
                ? 'Choose which appointments in this series you want to delete. This action cannot be undone.'
                : 'This will permanently delete this appointment. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {isRecurring && (
            <FormField
              control={form.control}
              name="deleteScope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delete scope</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="this_only">This event only</option>
                      {!isModified && <option value="this_and_following">This and following events</option>}
                      {!isModified && <option value="all">All events</option>}
                    </select>
                  </FormControl>
                  <FormDescription>
                    Choose which appointments in this series you want to delete
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                const deleteScope = isRecurring 
                  ? (form.getValues('deleteScope') || 'this_only')
                  : 'this_only';
                if (onDelete) {
                  onDelete(deleteScope);
                }
                setShowDeleteConfirmation(false);
              }}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
};

export default AppointmentForm;
